/**
 * v1.5 2FA 设置（ControlPanel 🔐 2FA 设置 tab）
 *
 * 双开关 + 角色勾选 + 双白名单
 * 后端：/admin/auth-config/{status, 2fa-disabled, 2fa-roles, 2fa-bypass-add, 2fa-bypass-remove}
 */
import { useState } from 'react';
import { Input, message, Tag, Popconfirm, Switch } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { Btn, Pill } from '../../components/ui';

type Status = {
  effective_enabled: boolean;
  env_disabled: boolean;
  kv_disabled: boolean;
  enabled_roles: string[];
  env_bypass_phones: string[];
  kv_bypass_phones: string[];
};

const ALL_ROLES = [
  { code: 'admin', label: 'admin（管理员）' },
  { code: 'director', label: 'director（总监）' },
  { code: 'manager', label: 'manager（主管）' },
  { code: 'sales', label: 'sales（销售）' },
  { code: 'academic', label: 'academic（教务员）' },
  { code: 'academic_director', label: 'academic_director（教务主管）' },
];

export function Auth2faConfig() {
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery<Status>({
    queryKey: ['auth-config-status'],
    queryFn: () => $.get('/admin/auth-config/status'),
    refetchInterval: 30_000,
  });

  const [newPhone, setNewPhone] = useState('');

  const toggleDisabled = useMutation({
    mutationFn: (disabled: boolean) => $.post('/admin/auth-config/2fa-disabled', { disabled }),
    onSuccess: () => {
      message.success('已更新（60s 内全员生效，缓存已主动失效）');
      qc.invalidateQueries({ queryKey: ['auth-config-status'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? '失败'),
  });

  const toggleRole = useMutation({
    mutationFn: (roles: string[]) => $.post('/admin/auth-config/2fa-roles', { roles }),
    onSuccess: () => {
      message.success('已更新（60s 内生效）');
      qc.invalidateQueries({ queryKey: ['auth-config-status'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? '失败'),
  });

  const addBypass = useMutation({
    mutationFn: (phone: string) => $.post('/admin/auth-config/2fa-bypass-add', { phone }),
    onSuccess: () => {
      message.success('已添加白名单');
      setNewPhone('');
      qc.invalidateQueries({ queryKey: ['auth-config-status'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? '失败'),
  });

  const removeBypass = useMutation({
    mutationFn: (phone: string) => $.post('/admin/auth-config/2fa-bypass-remove', { phone }),
    onSuccess: () => {
      message.success('已移除');
      qc.invalidateQueries({ queryKey: ['auth-config-status'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? '失败'),
  });

  if (isLoading || !status) return <div style={{ padding: 40, textAlign: 'center', color: '#8F959E' }}>加载中…</div>;

  return (
    <div>
      {/* 状态汇总 */}
      <div style={{
        padding: 16, marginBottom: 12, borderRadius: 8,
        border: '1px solid #DEE0E3',
        background: status.effective_enabled ? '#E8F7EE' : '#FEEBEC',
      }}>
        <div style={{ fontSize: 16, fontWeight: 500 }}>
          {status.effective_enabled ? '🔒 2FA 已启用' : '🔓 2FA 未启用'}
          {status.env_disabled && <Pill tone="warning">env 强制关闭</Pill>}
          {!status.env_disabled && status.kv_disabled && <Pill tone="warning">KV 临时关闭</Pill>}
        </div>
        <div style={{ fontSize: 12, color: '#646A73', marginTop: 6 }}>
          双开关：env <code>AUTH_DISABLE_2FA</code>={status.env_disabled ? '1' : '0'}
          {' / '}
          KV <code>auth.2fa_disabled</code>={String(status.kv_disabled)}
          {' · 任一为 true 则 2FA 关闭'}
        </div>
      </div>

      {/* 双开关 */}
      <Section title="🛑 全局开关">
        <Row label={
          <>
            <code>env AUTH_DISABLE_2FA</code>
            <Tag color="gold" style={{ marginLeft: 8 }}>终极开关 · 需改 .env</Tag>
          </>
        }>
          <span style={{ fontSize: 13, fontFamily: 'monospace', color: status.env_disabled ? '#FF6B00' : '#1A7F37' }}>
            {status.env_disabled ? '1 (强制关闭 2FA)' : '0 (未强制关闭)'}
          </span>
          <span style={{ marginLeft: 12, fontSize: 11, color: '#8F959E' }}>
            DB 挂时也能 SSH 改回滚；UI 只读
          </span>
        </Row>
        <Row label={
          <>
            <code>KV auth.2fa_disabled</code>
            <Tag color="blue" style={{ marginLeft: 8 }}>日常便捷 · UI 可改</Tag>
          </>
        }>
          <Switch
            checked={!status.kv_disabled}
            onChange={(checked) => toggleDisabled.mutate(!checked)}
            checkedChildren="启用"
            unCheckedChildren="临时关"
            disabled={toggleDisabled.isPending}
          />
          <span style={{ marginLeft: 12, fontSize: 11, color: '#8F959E' }}>
            日常临时关用（例如短信通道维护）；60s 内全员生效
          </span>
        </Row>
      </Section>

      {/* 角色范围 */}
      <Section title="👥 启用 2FA 的角色">
        <div style={{ fontSize: 12, color: '#646A73', marginBottom: 12 }}>
          仅勾选的角色登录时需要 2FA（其他角色不要求）。建议起步仅 admin，灰度往下扩。
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {ALL_ROLES.map((r) => {
            const checked = status.enabled_roles.includes(r.code);
            return (
              <label key={r.code} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                borderRadius: 6, border: '1px solid #DEE0E3', cursor: 'pointer',
                background: checked ? '#EFF2FE' : '#FFFFFF',
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? status.enabled_roles.filter((x) => x !== r.code)
                      : [...status.enabled_roles, r.code];
                    if (next.length === 0) return message.warning('至少保留 1 个角色');
                    toggleRole.mutate(next);
                  }}
                />
                <span style={{ fontSize: 13 }}>{r.label}</span>
              </label>
            );
          })}
        </div>
      </Section>

      {/* env 白名单（只读） */}
      <Section title="🛡️ env 白名单（保命通道·只读）">
        <div style={{ fontSize: 12, color: '#646A73', marginBottom: 8 }}>
          来源：<code>AUTH_2FA_BYPASS_PHONES</code> env，DB 出问题时仍可登录。如需改请 SSH 改 .env 重启。
        </div>
        {status.env_bypass_phones.length === 0 ? (
          <div style={{ padding: 12, color: '#FF6B00', fontSize: 12 }}>
            ⚠️ env 白名单为空。建议至少配 1 个 admin 手机号兜底（编辑 .env：<code>AUTH_2FA_BYPASS_PHONES=13800000001</code>）
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {status.env_bypass_phones.map((p) => (
              <Tag key={p} color="gold" style={{ fontSize: 12, padding: '2px 8px' }}>{p}</Tag>
            ))}
          </div>
        )}
      </Section>

      {/* KV 白名单（可加删） */}
      <Section title="📞 KV 白名单（UI 可加删）">
        <div style={{ fontSize: 12, color: '#646A73', marginBottom: 12 }}>
          这些号码即便 2FA 已启用也跳过验证（保命兜底）。60s 内生效。
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {status.kv_bypass_phones.length === 0
            ? <span style={{ fontSize: 12, color: '#8F959E' }}>无</span>
            : status.kv_bypass_phones.map((p) => (
              <Tag key={p} closable color="blue" style={{ fontSize: 12, padding: '2px 8px' }}
                   onClose={(e) => { e.preventDefault(); }}
                   closeIcon={
                     <Popconfirm title={`从白名单移除 ${p}？`}
                                 onConfirm={() => removeBypass.mutateAsync(p)}>
                       <span style={{ marginLeft: 4 }}>×</span>
                     </Popconfirm>}>
                {p}
              </Tag>
            ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input placeholder="11 位手机号" value={newPhone}
                 onChange={(e) => setNewPhone(e.target.value)} style={{ maxWidth: 200 }} />
          <Btn kind="primary" onClick={() => addBypass.mutate(newPhone)}
               disabled={!newPhone || addBypass.isPending}>
            + 添加
          </Btn>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: any }) {
  return (
    <div style={{ padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8, marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, children }: { label: any; children: any }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F0F2F5' }}>
      <div style={{ width: 280, fontSize: 13, color: '#646A73' }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13 }}>{children}</div>
    </div>
  );
}
